// frontend/src/components/ExpenseManagement/ExpenseForm.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; // Ajout de useMemo et useRef

import { apiService } from '../../services/api';

import { format } from 'date-fns';

import { Loader2, UploadCloud, Paperclip, XCircle, Trash2, Eye, EyeOff, FileUp, ChevronDown, Search } from 'lucide-react';

const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mo

const EMPTY_ALLOCATION = { project_id: '', iteration_id: '', percentage: '', amount_input: '' };

const formatAllocationAmount = (totalAmount, percentage) => {

  if (!Number.isFinite(totalAmount)) {

    return '';

  }

  const parsedPercentage = parseInt(percentage, 10);

  if (Number.isNaN(parsedPercentage) || parsedPercentage <= 0) {

    return '';

  }

  const amountValue = (totalAmount * parsedPercentage) / 100;

  const roundedValue = Math.round(amountValue * 100) / 100;

  const [integerPart, decimalPart = ''] = roundedValue.toString().split('.');

  const trimmedDecimal = decimalPart.replace(/0+$/, '');

  return trimmedDecimal ? `${integerPart},${trimmedDecimal}` : integerPart;

};

const sanitizeAmountInput = (rawValue) => {

  const cleaned = rawValue.replace(/[^0-9.,]/g, '');

  if (cleaned === '') {

    return { display: '', numeric: null };

  }

  let normalized = cleaned.replace(',', '.');

  if (normalized.startsWith('.')) {

    normalized = `0${normalized}`;

  }

  const parts = normalized.split('.');

  const integerPart = parts.shift() || '0';

  const decimalPart = parts.join('');

  let normalizedNumber = integerPart;

  if (decimalPart !== '') {

    normalizedNumber += `.${decimalPart}`;

  } else if (cleaned.endsWith('.') || cleaned.endsWith(',')) {

    normalizedNumber += '.';

  }

  const display = normalizedNumber.replace('.', ',');

  const numeric = normalizedNumber === '.' ? null : parseFloat(normalizedNumber);

  return {

    display,

    numeric: Number.isFinite(numeric) && numeric >= 0 ? numeric : null,

  };

};

const computeEqualDistribution = (count) => {

  if (count <= 0) {

    return [];

  }

  const base = Math.floor(100 / count);

  const remainder = 100 - base * count;

  const distribution = Array.from({ length: count }, () => base);

  for (let i = 0; i < remainder; i += 1) {

    const idx = count - 1 - i;

    distribution[idx] += 1;

  }

  return distribution;

};

const applyEqualDistribution = (allocations, totalAmountValue) => {

  if (!allocations || allocations.length === 0) {

    const percentageValue = '100';

    return [

      {

        ...EMPTY_ALLOCATION,

        percentage: percentageValue,

        amount_input:

          totalAmountValue && Number.isFinite(totalAmountValue)

            ? formatAllocationAmount(totalAmountValue, percentageValue)

            : '',

      },

    ];

  }

  const distribution = computeEqualDistribution(allocations.length);

  return allocations.map((allocation, index) => {

    const percentageValue = String(distribution[index]);

    return {

      ...allocation,

      percentage: percentageValue,

      amount_input:

        totalAmountValue && Number.isFinite(totalAmountValue)

          ? formatAllocationAmount(totalAmountValue, percentageValue)

          : allocation.amount_input || '',

    };

  });

};

const PROJECT_STATUS_META = {

  in_progress: {

    label: 'En cours',

    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',

  },

  proposition: {

    label: 'Proposition',

    badgeClass: 'border-sky-200 bg-sky-50 text-sky-700',

  },

  completed: {

    label: 'Terminé',

    badgeClass: 'border-slate-200 bg-slate-50 text-slate-600',

  },

  default: {

    label: 'Projet',

    badgeClass: 'border-gray-200 bg-gray-50 text-gray-600',

  },

};

const getProjectStatusMeta = (status) => {

  if (!status) { return PROJECT_STATUS_META.default; }

  const normalized = String(status).toLowerCase();

  return PROJECT_STATUS_META[normalized] || PROJECT_STATUS_META.default;

};

const ProjectStatusBadge = ({ status }) => {

  const meta = getProjectStatusMeta(status);

  return (

    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border ${meta.badgeClass}`}>

      {meta.label}

    </span>

  );

};

const ProjectPicker = ({ projects = [], value, onSelect, error = false, placeholder = 'Sélectionner un projet' }) => {

  const [isOpen, setIsOpen] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const containerRef = useRef(null);

  const inputRef = useRef(null);

  const normalizedValue = value ? String(value) : '';

  useEffect(() => {

    if (!isOpen) { return undefined; }

    const handleClickOutside = (event) => {

      if (containerRef.current && !containerRef.current.contains(event.target)) {

        setIsOpen(false);

      }

    };

    const handleKeyDown = (event) => {

      if (event.key === 'Escape') {

        setIsOpen(false);

      }

    };

    document.addEventListener('mousedown', handleClickOutside);

    document.addEventListener('keydown', handleKeyDown);

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

      document.removeEventListener('keydown', handleKeyDown);

    };

  }, [isOpen]);

  const sortedProjects = useMemo(() => {

    if (!Array.isArray(projects)) { return []; }

    const priority = (status) => {

      const normalized = String(status || '').toLowerCase();

      if (normalized === 'in_progress') { return 0; }

      if (normalized === 'proposition') { return 1; }

      if (normalized === 'completed') { return 2; }

      return 3;

    };

    return [...projects].sort((a, b) => {

      const diff = priority(a.status) - priority(b.status);

      if (diff !== 0) { return diff; }

      return (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });

    });

  }, [projects]);

  const filteredProjects = useMemo(() => {

    const term = searchValue.trim().toLowerCase();

    if (!term) { return sortedProjects; }

    return sortedProjects.filter((project) => {

      const haystack = [

        project.name,

        project.project_number,

        project.client_name,

      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(term);

    });

  }, [sortedProjects, searchValue]);

  const groupedProjects = useMemo(() => {

    if (!filteredProjects.length) { return []; }

    const term = searchValue.trim();

    if (term) {

      return [{

        label: `Résultats (${filteredProjects.length})`,

        items: filteredProjects,

      }];

    }

    const inProgress = [];

    const others = [];

    filteredProjects.forEach((project) => {

      if (String(project.status || '').toLowerCase() === 'in_progress') {

        inProgress.push(project);

      } else {

        others.push(project);

      }

    });

    const groups = [];

    if (inProgress.length) {

      groups.push({ label: 'Projets en cours', items: inProgress });

    }

    if (others.length) {

      groups.push({ label: 'Autres projets', items: others });

    }

    return groups;

  }, [filteredProjects, searchValue]);

  const selectedProject = useMemo(

    () => sortedProjects.find((project) => String(project.id) === normalizedValue) || null,

    [sortedProjects, normalizedValue],

  );

  const handleSelectProject = (projectId) => {

    if (onSelect) {

      onSelect(String(projectId));

    }

    setIsOpen(false);

    setSearchValue('');

    requestAnimationFrame(() => {

      if (inputRef.current) {

        inputRef.current.blur();

      }

    });

  };

  const handleInputFocus = () => {

    setIsOpen(true);

    setSearchValue('');

    requestAnimationFrame(() => {

      if (inputRef.current) {

        inputRef.current.select();

      }

    });

  };

  const handleInputChange = (event) => {

    const { value: nextValue } = event.target;

    if (!isOpen) {

      setIsOpen(true);

    }

    setSearchValue(nextValue);

  };

  const handleToggle = (event) => {

    event.preventDefault();

    event.stopPropagation();

    setIsOpen((prev) => {

      const next = !prev;

      if (next) {

        setSearchValue('');

        requestAnimationFrame(() => {

          inputRef.current?.focus();

          inputRef.current?.select();

        });

      }

      return next;

    });

  };

  const displayedInputValue = isOpen ? searchValue : (selectedProject ? selectedProject.name : '');

  return (

    <div ref={containerRef} className="relative">

      <div

        className={`flex items-center gap-2 border rounded-md bg-white shadow-sm transition focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-500 ${

          error ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300'

        }`}

      >

        <Search className="ml-2 h-4 w-4 text-gray-400" />

        <input

          ref={inputRef}

          type="text"

          value={displayedInputValue}

          onFocus={handleInputFocus}

          onChange={handleInputChange}

          placeholder={placeholder}

          className="flex-1 py-1.5 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"

        />

        {selectedProject && !isOpen && (

          <div className="hidden sm:block pr-1.5">

            <ProjectStatusBadge status={selectedProject.status} />

          </div>

        )}

        <button

          type="button"

          onMouseDown={handleToggle}

          className="px-2 py-1 text-gray-400 hover:text-gray-600 focus:outline-none"

          aria-label={isOpen ? 'Fermer la sélection projet' : 'Ouvrir la sélection projet'}

        >

          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />

        </button>

      </div>

      {selectedProject?.client_name && (

        <p className="mt-0.5 text-2xs text-gray-500 truncate">

          {selectedProject.client_name}

        </p>

      )}

      {isOpen && (

        <div className="absolute z-40 mt-1.5 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">

          <div className="max-h-[70vh] overflow-y-auto">

            {groupedProjects.length === 0 && (

              <div className="px-3 py-4 text-sm text-gray-500 text-center">

                Aucun projet ne correspond à votre recherche.

              </div>

            )}

            {groupedProjects.map((group) => (

              <div key={group.label}>

                <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">

                  {group.label}

                </div>

                <div className="divide-y divide-gray-100">

                  {group.items.map((project) => {

                    const meta = getProjectStatusMeta(project.status);

                    const isActive = String(project.id) === normalizedValue;

                    return (

                      <button

                        key={project.id}

                        type="button"

                        onClick={() => handleSelectProject(project.id)}

                        className={`w-full px-3 py-1.5 text-left text-sm flex items-start justify-between gap-3 transition ${

                          isActive ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50'

                        }`}

                      >

                        <div className="flex-1 min-w-0">

                          <p className="font-medium text-gray-900 truncate">{project.name}</p>

                          <div className="flex flex-wrap items-center gap-1 text-2xs text-gray-500 mt-0.5">

                            {project.project_number && <span>{project.project_number}</span>}

                            {project.client_name && (

                              <span className="hidden sm:inline-block">• {project.client_name}</span>

                            )}

                          </div>

                          {project.vision && (

                            <p className="text-2xs text-gray-500 mt-0.5 line-clamp-2">{project.vision}</p>

                          )}

                        </div>

                        <span className={`flex-shrink-0 px-2 py-0.5 text-[11px] rounded-full border ${meta.badgeClass}`}>

                          {meta.label}

                        </span>

                      </button>

                    );

                  })}

                </div>

              </div>

            ))}

          </div>

        </div>

      )}

    </div>

  );

};

const IterationPicker = ({

  iterations = [],

  value,

  onSelect,

  placeholder = '— Aucune —',

  disabled = false,

  loading = false,

  error = null,

}) => {

  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef(null);

  const normalizedValue = value ? String(value) : '';

  useEffect(() => {

    if (!isOpen) { return undefined; }

    const handleClickOutside = (event) => {

      if (containerRef.current && !containerRef.current.contains(event.target)) {

        setIsOpen(false);

      }

    };

    const handleKeyDown = (event) => {

      if (event.key === 'Escape') {

        setIsOpen(false);

      }

    };

    document.addEventListener('mousedown', handleClickOutside);

    document.addEventListener('keydown', handleKeyDown);

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

      document.removeEventListener('keydown', handleKeyDown);

    };

  }, [isOpen]);

  useEffect(() => {

    if (disabled) {

      setIsOpen(false);

    }

  }, [disabled]);

  const selectedIteration = useMemo(

    () => iterations.find((iteration) => String(iteration.id) === normalizedValue) || null,

    [iterations, normalizedValue],

  );

  const selectedStatus = selectedIteration ? getIterationStatusMeta(selectedIteration) : null;

  const selectedVisionRaw = selectedIteration

    ? (selectedIteration.description ?? selectedIteration.vision ?? '')

    : '';

  const selectedVision = typeof selectedVisionRaw === 'string' ? selectedVisionRaw.trim() : '';

  const handleSelect = (iterationId) => {

    if (onSelect) {

      onSelect(iterationId);

    }

    setIsOpen(false);

  };

  return (

    <div ref={containerRef} className="relative">

      <button

        type="button"

        onClick={() => { if (!disabled) { setIsOpen((prev) => !prev); } }}

        disabled={disabled}

        className={`w-full px-2.5 py-1.5 border rounded-md text-left text-sm flex items-center justify-between gap-3 transition-colors ${

          disabled

            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'

            : 'bg-white border-gray-300 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-100 focus-visible:border-blue-500'

        }`}

        aria-haspopup="listbox"

        aria-expanded={isOpen}

      >

        <div className="flex flex-col min-w-0 flex-1">

          <span className={`truncate ${selectedIteration ? 'text-gray-900 font-medium' : 'text-gray-400 italic'}`}>

            {selectedIteration ? formatIterationOptionLabel(selectedIteration) : placeholder}

          </span>

          {selectedIteration && (

            <span className="text-2xs text-gray-500 truncate">

              {selectedVision || 'Pas de vision renseignée'}

            </span>

          )}

        </div>

        <div className="flex items-center gap-2 flex-shrink-0">

          {selectedStatus && (

            <span className={`px-2 py-0.5 text-[11px] rounded-full border ${selectedStatus.badgeClass}`}>

              {selectedStatus.label}

            </span>

          )}

          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />

        </div>

      </button>

      {isOpen && (

        <div className="absolute z-40 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">

          {loading ? (

            <div className="flex items-center justify-center gap-2 px-3 py-3 text-sm text-gray-500">

              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />

              Chargement des itérations…

            </div>

          ) : (

            <div className="max-h-[70vh] overflow-y-auto">

              <button

                type="button"

                onClick={() => handleSelect('')}

                className={`w-full px-3 py-1.5 text-left text-sm transition hover:bg-gray-50 ${normalizedValue === '' ? 'bg-indigo-50 text-indigo-700' : ''}`}

              >

                ⎯ Aucune itération

              </button>

              {iterations.length === 0 && (

                <div className="px-3 py-4 text-sm text-gray-500 text-center">

                  Aucune itération disponible pour ce projet.

                </div>

              )}

              <div className="divide-y divide-gray-100">

                {iterations.map((iteration) => {

                  const statusMeta = getIterationStatusMeta(iteration);

                  const iterationVisionRaw = iteration.description ?? iteration.vision ?? '';

                  const iterationVision = typeof iterationVisionRaw === 'string' ? iterationVisionRaw.trim() : '';

                  const isActive = String(iteration.id) === normalizedValue;

                  return (

                    <button

                      key={iteration.id}

                      type="button"

                      onClick={() => handleSelect(String(iteration.id))}

                      className={`w-full px-3 py-1.5 text-left text-sm flex flex-col gap-1 transition ${isActive ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-900'}`}

                    >

                      <span className="flex items-center gap-2">

                        <span className="font-medium truncate">{iteration.name || `Itération ${iteration.id}`}</span>

                        <span className={`px-2 py-0.5 text-[11px] rounded-full border ${statusMeta.badgeClass}`}>

                          {statusMeta.label}

                        </span>

                      </span>

                      {iterationVision ? (

                        <span className="text-2xs text-gray-500 leading-snug line-clamp-2">

                          {iterationVision}

                        </span>

                      ) : (

                        <span className="text-2xs text-gray-400 italic">Pas de vision renseignée.</span>

                      )}

                    </button>

                  );

                })}

              </div>

            </div>

          )}

          {error && (

            <div className="px-3 py-2 text-xs text-red-500 bg-red-50 border-t border-red-100">

              {error}

            </div>

          )}

        </div>

      )}

    </div>

  );

};

const getIterationStatusMeta = (iteration) => {

  if (!iteration) {

    return { key: 'unknown', label: 'Statut inconnu', badgeClass: 'border-gray-200 bg-gray-50 text-gray-600' };

  }

  const { start_date: startDateRaw, end_date: endDateRaw } = iteration;

  const startDate = startDateRaw ? new Date(startDateRaw) : null;

  const endDate = endDateRaw ? new Date(endDateRaw) : null;

  if (!startDate || Number.isNaN(startDate.getTime()) || !endDate || Number.isNaN(endDate.getTime())) {

    return { key: 'unknown', label: 'Dates à confirmer', badgeClass: 'border-amber-200 bg-amber-50 text-amber-700' };

  }

  const today = new Date();

  if (today < startDate) {

    return { key: 'upcoming', label: 'À venir', badgeClass: 'border-sky-200 bg-sky-50 text-sky-700' };

  }

  if (today > endDate) {

    return { key: 'completed', label: 'Terminée', badgeClass: 'border-slate-200 bg-slate-50 text-slate-600' };

  }

  return { key: 'active', label: 'En cours', badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700' };

};

const formatIterationOptionLabel = (iteration) => {

  if (!iteration) { return ''; }

  const base = iteration.name || `Itération ${iteration.id}`;

  const status = getIterationStatusMeta(iteration);

  return status?.label ? `${base} • ${status.label}` : base;

};

const ExpenseForm = ({ onSubmit, projects, employeeId, onCancel, initialData = null, apiBaseUrl }) => {

  const isEditing = initialData !== null;

  const [step, setStep] = useState(isEditing ? 'form' : 'upload');

  const [formData, setFormData] = useState(() => {

    const defaultState = {

      amount: '',

      tva: '',

      credit_note: false,

      description: '',

      date: format(new Date(), 'yyyy-MM-dd'),

      project_allocations: [{ ...EMPTY_ALLOCATION, percentage: '100' }],

      file: null,

      existingFilePath: null,

      currency: '€',

      vendor: '',

      is_amazon_document: false,

    };

    if (!initialData) {

      return defaultState;

    }

    const initialAmountNumber =

      initialData.amount != null ? Math.abs(parseFloat(String(initialData.amount))) : null;

    const mappedAllocations =

      initialData.project_allocations?.length > 0

        ? initialData.project_allocations.map((allocation) => {

            const percentageValue =

              allocation.percentage != null ? String(Math.round(allocation.percentage)) : '';

            return {

              project_id: allocation.project_id,

              iteration_id: allocation.iteration_id ? String(allocation.iteration_id) : '',

              percentage: percentageValue,

              amount_input:

                initialAmountNumber != null

                  ? formatAllocationAmount(initialAmountNumber, percentageValue)

                  : '',

            };

          })

        : [{ ...EMPTY_ALLOCATION, percentage: '100' }];

    return {

      // Un avoir est enregistré en négatif ; le formulaire montre les montants imprimés et la case « Avoir ».
      amount:

        initialData.amount != null ? String(Math.abs(initialData.amount)).replace('.', ',') : '',

      tva: initialData.tva != null ? String(Math.abs(initialData.tva)).replace('.', ',') : '',

      credit_note: Number(initialData.amount) < 0,

      description: initialData.description || '',

      date: initialData.date

        ? format(new Date(initialData.date), 'yyyy-MM-dd')

        : format(new Date(), 'yyyy-MM-dd'),

      project_allocations: mappedAllocations,

      file: null,

      existingFilePath: initialData.file_path || null,

      currency: initialData.currency || '€',

      vendor: initialData.vendor || '',

      is_amazon_document: false,

    };

  });

  const [filePreviewUrl, setFilePreviewUrl] = useState(null);

  const [fileError, setFileError] = useState('');

  const [uploadProgress, setUploadProgress] = useState(0);

  const [isUploading, setIsUploading] = useState(false);

  const [isAiExtracting, setIsAiExtracting] = useState(false);

  const [showPreview, setShowPreview] = useState(true);

  const [editErrors, setEditErrors] = useState({});

  const [iterationsByProject, setIterationsByProject] = useState({});

  const projectsById = useMemo(() => {

    if (!Array.isArray(projects)) { return {}; }

    return projects.reduce((acc, project) => {

      acc[String(project.id)] = project;

      return acc;

    }, {});

  }, [projects]);

  const totalAmountValue = useMemo(() => {

    const normalized = String(formData.amount || '').replace(',', '.');

    const parsed = parseFloat(normalized);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;

  }, [formData.amount]);

  const allocationStats = useMemo(() => {

    const allocations = formData.project_allocations || [];

    const total = allocations.reduce((sum, alloc) => {

      const value = parseInt(alloc.percentage, 10);

      return sum + (Number.isNaN(value) ? 0 : value);

    }, 0);

    const hasProjects = allocations.some(alloc => alloc.project_id);

    return {

      total,

      missing: 100 - total,

      hasProjects,

    };

  }, [formData.project_allocations]);

  const clearAllocationErrors = useCallback(() => {

    setEditErrors(prev => {

      const { allocations, form, ...rest } = prev;

      return rest;

    });

  }, []);

  const loadIterationsForProject = useCallback(async (projectId) => {

    if (!projectId) {

      return;

    }

    setIterationsByProject(prev => {

      const existing = prev[projectId];

      if (existing && (existing.loading || (existing.items && existing.items.length > 0))) {

        return prev;

      }

      return {

        ...prev,

        [projectId]: { items: existing?.items || [], loading: true, error: null },

      };

    });

    try {

      const data = await apiService.get(`/projects/${projectId}/iterations`);

      setIterationsByProject(prev => ({

        ...prev,

        [projectId]: { items: Array.isArray(data) ? data : [], loading: false, error: null },

      }));

    } catch (error) {

      setIterationsByProject(prev => ({

        ...prev,

        [projectId]: { items: [], loading: false, error: error?.message || 'Erreur chargement itérations' },

      }));

    }

  }, []);

  useEffect(() => {

    const uniqueProjectIds = Array.from(

      new Set((formData.project_allocations || []).map(a => a.project_id).filter(Boolean))

    );

    uniqueProjectIds.forEach(pid => {

      loadIterationsForProject(pid);

    });

  }, [formData.project_allocations, loadIterationsForProject]);

  const updateAllocation = useCallback((index, updater) => {

    setFormData(prev => {

      const allocs = [...(prev.project_allocations || [])];

      const current = allocs[index] || { ...EMPTY_ALLOCATION };

      allocs[index] = updater(current);

      return { ...prev, project_allocations: allocs };

    });

    clearAllocationErrors();

  }, [clearAllocationErrors]);

  const handleProjectSelect = useCallback((index, value) => {

    updateAllocation(index, alloc => ({

      ...alloc,

      project_id: value,

      iteration_id: '',

    }));

    if (value) {

      loadIterationsForProject(value);

    }

  }, [updateAllocation, loadIterationsForProject]);

  const handleIterationSelect = useCallback((index, value) => {

    updateAllocation(index, alloc => ({

      ...alloc,

      iteration_id: value,

    }));

  }, [updateAllocation]);

  const handlePercentageChange = useCallback((index, value) => {

    const digitsOnly = value.replace(/[^0-9]/g, '');

    if (digitsOnly === '') {

      updateAllocation(index, (alloc) => ({ ...alloc, percentage: '', amount_input: '' }));

      return;

    }

    const parsed = parseInt(digitsOnly, 10);

    const clamped = Math.min(100, parsed);

    const nextPercentage = clamped === 0 ? '' : String(clamped);

    updateAllocation(index, (alloc) => ({

      ...alloc,

      percentage: nextPercentage,

      amount_input:

        totalAmountValue && nextPercentage !== ''

          ? formatAllocationAmount(totalAmountValue, nextPercentage)

          : '',

    }));

  }, [totalAmountValue, updateAllocation]);

  const handleAllocationAmountChange = useCallback((index, value) => {

    const { display, numeric } = sanitizeAmountInput(value);

    const numericValue = typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : null;

    if (numericValue === null || numericValue < 0) {

      updateAllocation(index, (alloc) => ({ ...alloc, percentage: '', amount_input: display }));

      return;

    }

    if (!totalAmountValue || !Number.isFinite(totalAmountValue) || totalAmountValue <= 0) {

      updateAllocation(index, (alloc) => ({ ...alloc, amount_input: display }));

      return;

    }

    const pct = Math.min(100, Math.max(0, Math.round((numericValue / totalAmountValue) * 100)));

    updateAllocation(index, (alloc) => ({

      ...alloc,

      percentage: pct === 0 ? '' : String(pct),

      amount_input: display,

    }));

  }, [totalAmountValue, updateAllocation]);

  useEffect(() => {

    let objectUrl = null;

    const pdfParams = '#toolbar=0&navpanes=0&view=FitH';

    if (formData.file) {

      objectUrl = URL.createObjectURL(formData.file);

      if (formData.file.type === 'application/pdf') {

        setFilePreviewUrl(objectUrl + pdfParams);

      } else {

        setFilePreviewUrl(objectUrl);

      }

      setShowPreview(true);

    } else if (isEditing && formData.existingFilePath) {

      const serverUrl = `${apiBaseUrl}/expenses/files/${formData.existingFilePath}`;

      if (formData.existingFilePath.toLowerCase().endsWith('.pdf')) {

        setFilePreviewUrl(serverUrl + pdfParams);

      } else {

        setFilePreviewUrl(serverUrl);

      }

      setShowPreview(true);

    } else {

      setFilePreviewUrl(null);

    }

    return () => {

      if (objectUrl) {

        URL.revokeObjectURL(objectUrl);

      }

    };

  }, [formData.file, isEditing, formData.existingFilePath, apiBaseUrl]);

  const handleChange = (e) => {

    const { name, value } = e.target;

    if (name === 'amount') {

      const { display, numeric } = sanitizeAmountInput(value);

      const numericValue = typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : null;

      setFormData((prev) => {

        const updatedAllocations = (prev.project_allocations || []).map((alloc) => {

          if (numericValue === null || numericValue <= 0) {

            return { ...alloc, amount_input: '' };

          }

          if (alloc.percentage === '' || Number.isNaN(parseInt(alloc.percentage, 10))) {

            return { ...alloc, amount_input: '' };

          }

          return {

            ...alloc,

            amount_input: formatAllocationAmount(numericValue, alloc.percentage),

          };

        });

        return { ...prev, amount: display, project_allocations: updatedAllocations };

      });

      setEditErrors((prev) => {

        const { amount, form, ...rest } = prev;

        return rest;

      });

      return;

    }

    if (name === 'tva') {

      const { display } = sanitizeAmountInput(value);

      setFormData((prev) => ({ ...prev, tva: display }));

      setEditErrors((prev) => {

        const { tva, form, ...rest } = prev;

        return rest;

      });

      return;

    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    setEditErrors((prev) => {

      const { [name]: _, form: __, ...rest } = prev;

      return rest;

    });

  };

  const addAllocation = () => {

    setFormData(prev => {

      const currentAllocations = prev.project_allocations || [];

      const expandedAllocations = [...currentAllocations, { ...EMPTY_ALLOCATION }];

      const redistributed = applyEqualDistribution(expandedAllocations, totalAmountValue);

      return {

        ...prev,

        project_allocations: redistributed,

      };

    });

    clearAllocationErrors();

  };

  const removeAllocation = index => {

    setFormData(prev => {

      const nextAllocations = prev.project_allocations.filter((_, i) => i !== index);

      const updatedAllocations = nextAllocations.length === 0

        ? applyEqualDistribution([], totalAmountValue)

        : applyEqualDistribution(nextAllocations, totalAmountValue);

      return {

        ...prev,

        project_allocations: updatedAllocations,

      };

    });

    clearAllocationErrors();

  };

  const handleFileChange = async (e) => {

    const file = e.target.files[0];

    setFormData(prev => ({

      ...prev,

      file: null,

      existingFilePath: isEditing ? initialData?.file_path : null,

      is_amazon_document: false

    }));

    setFileError('');

    if (!file) { return; }

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) { setFileError('Format non accepté.'); return; }

    if (file.size > MAX_FILE_SIZE) { setFileError('Fichier trop volumineux (Max 5 Mo).'); return; }

    const currentFile = file;

    setFormData(prev => ({

      ...prev,

      file: currentFile,

      existingFilePath: null,

      is_amazon_document: false

    }));

    setStep('form');

    setIsAiExtracting(true);

    const fd = new FormData();

    fd.append('file', currentFile);

    try {

      const aiData = await apiService.uploadFile('/expenses/ai-extract', fd);

      let normalizedCurrency = aiData.currency ?? formData.currency;

      if (typeof normalizedCurrency === 'string') {

        const upperCurrency = normalizedCurrency.toUpperCase();

        if (upperCurrency === 'EUR') normalizedCurrency = '€';

        else if (upperCurrency === 'USD') normalizedCurrency = '$';

        else if (!['€', '$'].includes(normalizedCurrency)) normalizedCurrency = '€';

      } else {

        normalizedCurrency = '€';

      }

      const rawTva = aiData.tva ?? formData.tva;

      const sanitizedTva = rawTva != null ? sanitizeAmountInput(String(rawTva)) : null;
      const tvaNumeric = sanitizedTva && typeof sanitizedTva.numeric === 'number' && Number.isFinite(sanitizedTva.numeric)
        ? sanitizedTva.numeric
        : null;

      const aiTotalHtRaw = aiData.total_ht ?? aiData.totalHT ?? null;
      const aiTotalTtcRaw = aiData.total_ttc ?? aiData.total ?? null;
      const sanitizedAiTotalHt = aiTotalHtRaw != null ? sanitizeAmountInput(String(aiTotalHtRaw)) : null;
      const sanitizedAiTotalTtc = aiTotalTtcRaw != null ? sanitizeAmountInput(String(aiTotalTtcRaw)) : null;

      setFormData(prev => {

        const previousAmountSanitized = sanitizeAmountInput(prev.amount || '');

        let nextAmountDisplay = prev.amount;
        let nextAmountNumeric = typeof previousAmountSanitized.numeric === 'number' && Number.isFinite(previousAmountSanitized.numeric)
          ? previousAmountSanitized.numeric
          : null;

        if (sanitizedAiTotalHt && typeof sanitizedAiTotalHt.numeric === 'number' && Number.isFinite(sanitizedAiTotalHt.numeric)) {
          nextAmountDisplay = sanitizedAiTotalHt.display;
          nextAmountNumeric = sanitizedAiTotalHt.numeric;
        } else if (sanitizedAiTotalTtc && typeof sanitizedAiTotalTtc.numeric === 'number' && Number.isFinite(sanitizedAiTotalTtc.numeric)) {
          const ttcValue = sanitizedAiTotalTtc.numeric;
          const htValue = (tvaNumeric != null && Number.isFinite(tvaNumeric))
            ? Math.max(0, ttcValue - tvaNumeric)
            : ttcValue;
          const sanitizedHt = sanitizeAmountInput(String(htValue));
          if (sanitizedHt.display !== '') {
            nextAmountDisplay = sanitizedHt.display;
          }
          nextAmountNumeric = sanitizedHt.numeric;
        }

        const updatedAllocations = (prev.project_allocations || []).map((alloc) => {

          if (nextAmountNumeric === null || nextAmountNumeric <= 0) {

            return { ...alloc, amount_input: '' };

          }

          if (alloc.percentage === '' || Number.isNaN(parseInt(alloc.percentage, 10))) {

            return { ...alloc, amount_input: '' };

          }

          return {

            ...alloc,

            amount_input: formatAllocationAmount(nextAmountNumeric, alloc.percentage),

          };

        });

        return {

          ...prev,

          amount: nextAmountDisplay,

          tva: sanitizedTva ? sanitizedTva.display : prev.tva,

          credit_note: typeof aiData.credit_note === 'boolean' ? aiData.credit_note : prev.credit_note,

          vendor: aiData.vendor ?? prev.vendor,

          description: aiData.description ?? prev.description,

          date: aiData.date ? format(new Date(aiData.date), 'yyyy-MM-dd') : prev.date,

          currency: normalizedCurrency,

          is_amazon_document:

            aiData.is_amazon_document === true ||

            String(aiData.is_amazon_document || '').toLowerCase() === 'true',

          file: currentFile, 

          existingFilePath: null,

          project_allocations: updatedAllocations,

        };

      });

    } catch (err) {

      console.error('Erreur extraction IA :', err);

      setFileError(`Erreur IA: ${err.message || 'Inconnue'}`);

      setFormData(prev => ({

        ...prev,

        file: currentFile,

        existingFilePath: null,

        is_amazon_document: false

      }));

    } finally {

      setIsAiExtracting(false);

    }

  };

  const handleRemoveFile = () => {

    setFormData(prev => ({

      ...prev,

      file: null, 

      existingFilePath: isEditing ? (initialData?.file_path || null) : null,

      is_amazon_document: false

    }));

    setFileError('');

  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!isEditing && !employeeId) { alert("ID employé non valide."); return; }

    let currentErrors = {};

    const amountStr = String(formData.amount || '').replace(',', '.');

    const amountNum = parseFloat(amountStr);

    if (!formData.amount || isNaN(amountNum) || amountNum <= 0) currentErrors.amount = 'Montant HT positif requis.';

    const tvaStr = String(formData.tva || '').replace(',', '.');

    let tvaNum = null;

    if (formData.tva && formData.tva.trim() !== '') { tvaNum = parseFloat(tvaStr); if (isNaN(tvaNum)) currentErrors.tva = 'TVA invalide.'; }

    if (!formData.date) currentErrors.date = 'Date requise.';

    const normalizedAllocations = (formData.project_allocations || []).map(alloc => ({

      projectId: alloc.project_id ? parseInt(alloc.project_id, 10) : null,

      percentage: alloc.percentage === '' ? null : parseInt(alloc.percentage, 10),

      iterationId: alloc.iteration_id ? parseInt(alloc.iteration_id, 10) : null,

    }));

    const allocationsToSend = normalizedAllocations

      .filter(a => a.projectId !== null && a.percentage !== null && !Number.isNaN(a.percentage) && a.percentage > 0)

      .map(a => ({

        project_id: a.projectId,

        percentage: a.percentage,

        iteration_id: a.iterationId ?? null,

      }));

    const allocationErrors = [];

    normalizedAllocations.forEach(alloc => {

      if (alloc.projectId !== null) {

        if (alloc.percentage === null || Number.isNaN(alloc.percentage) || alloc.percentage <= 0 || alloc.percentage > 100) {

          allocationErrors.push('% invalide (>0, <=100) pour un projet sélectionné.');

        }

      } else if (alloc.percentage !== null && !Number.isNaN(alloc.percentage) && alloc.percentage > 0) {

        allocationErrors.push('Projet requis si % défini.');

      }

    });

    if (allocationsToSend.length > 0) {

      const totalPercentage = allocationsToSend.reduce((sum, alloc) => sum + alloc.percentage, 0);

      if (totalPercentage !== 100) {

        allocationErrors.push('La somme des pourcentages doit être 100%.');

      }

    }

    if (allocationErrors.length > 0) {

      currentErrors.allocations = Array.from(new Set(allocationErrors)).join(' ');

    }

    setEditErrors(currentErrors);

    if (Object.keys(currentErrors).length > 0) { console.log('Erreurs validation:', currentErrors); return; }

    const submitData = new FormData();

    const currentEmployeeId = isEditing ? initialData.employee_id : employeeId;

    if (!isEditing) submitData.append('employee_id', String(currentEmployeeId));

    // Avoir : compté en négatif, il rend du budget au projet.
    const sign = formData.credit_note ? -1 : 1;

    submitData.append('amount', String(sign * amountNum));

    submitData.append('tva', tvaNum !== null && !isNaN(tvaNum) ? String(sign * Math.abs(tvaNum)) : '');

    submitData.append('description', formData.description || '');

    submitData.append('date', formData.date);

    submitData.append('currency', formData.currency);

    submitData.append('vendor', formData.vendor || '');

    submitData.append('is_amazon_document', formData.is_amazon_document ? 'true' : 'false');

    if (allocationsToSend.length > 0) {

      submitData.append('project_allocations_json', JSON.stringify(allocationsToSend));

    }

    if (formData.file) submitData.append('file', formData.file);

    setIsUploading(true); setUploadProgress(0);

    try { await onSubmit(submitData, initialData?.id); }

    catch (error) { console.error('Erreur soumission (formulaire):', error); setEditErrors(prev => ({...prev, form: `Erreur: ${error.message || "Inconnue"}`})); }

    finally { setIsUploading(false); }

  };

  const calculateTotalTTC = () => { const aStr=String(formData.amount||'').replace(',','.'), tStr=String(formData.tva||'').replace(',','.'), aNum=parseFloat(aStr), tNum=parseFloat(tStr); if(!isNaN(aNum)){const ttc=aNum+(!isNaN(tNum)?tNum:0);return ttc.toFixed(2).replace('.',',');} return ''; };

  const totalTTC = calculateTotalTTC();

  return (

    <div className={`p-3 mb-4 relative ${isEditing ? 'bg-blue-50 border border-blue-200 rounded-lg' : 'bg-white rounded-lg shadow-md border'}`}>

      <h3 className="text-md font-semibold mb-3">{isEditing ? `Modifier Dépense #${initialData.id}` : 'Nouvelle dépense'}</h3>

      {step === 'upload' && !isEditing && (

        <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer bg-gray-50"

             onClick={() => document.getElementById(`file-upload-initial-${employeeId}`)?.click()}>

          <FileUp className="w-12 h-12 text-blue-500 mb-3" />

          <p className="text-lg font-semibold text-gray-700 mb-1">Ajouter un justificatif</p>

          <p className="text-sm text-gray-500 mb-4">Cliquez ou glissez-déposez (PDF, JPG, PNG - max 5Mo)</p>

          <span className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md text-sm shadow-sm">

            Choisir un fichier

          </span>

          <input

            id={`file-upload-initial-${employeeId}`}

            type="file"

            name="file"

            onChange={handleFileChange}

            accept=".pdf, .jpeg, .jpg, .png"

            className="hidden"

          />

          {fileError && (<p className="mt-3 text-sm text-red-600">{fileError}</p>)}

          <button type="button" onClick={onCancel} className="mt-4 text-xs text-gray-500 hover:underline">Annuler</button>

        </div>

      )}

      {step === 'form' && (

        <>

          {(isAiExtracting || isUploading) && (

             <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10 rounded-lg">

               <Loader2 className="animate-spin h-5 w-5 text-blue-500" />

               <span className="ml-2 text-sm font-medium">{isAiExtracting ? "Analyse..." : (isEditing ? "Sauvegarde..." : "Envoi...")}</span>

             </div>

          )}

          <div className="flex flex-col gap-3 lg:flex-row">

            <form onSubmit={handleSubmit} className="space-y-2 flex-grow min-w-0 lg:w-2/3 lg:relative lg:z-10">

              <fieldset disabled={isAiExtracting || isUploading} className="space-y-2">

                <fieldset className="border p-2 rounded-md border-gray-300 bg-white">

                  <legend className="text-xs font-medium px-1 text-gray-600">Montants</legend>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">

                    <div> <label className="block text-2xs font-medium text-gray-700 mb-0.5">Montant HT *</label> <div className="flex space-x-1"> <input type="text" name="amount" placeholder="ex: 123,45" value={formData.amount} onChange={handleChange} className={`flex-grow p-1.5 border rounded-md text-xs shadow-sm ${editErrors.amount ? 'border-red-500' : 'border-gray-300'}`} required inputMode="decimal" /> <select name="currency" value={formData.currency} onChange={handleChange} className="p-1.5 border border-gray-300 rounded-md text-xs bg-white shadow-sm w-14"><option value="€">€</option><option value="$">$</option></select> </div> {editErrors.amount && <p className="text-2xs text-red-500 mt-0.5">{editErrors.amount}</p>} <label className="mt-1 flex items-center gap-1.5 text-2xs text-gray-700"><input type="checkbox" checked={!!formData.credit_note} onChange={(e) => setFormData((prev) => ({ ...prev, credit_note: e.target.checked }))} /> Avoir (remboursement : rend du budget au projet)</label> </div>

                    <div> <label className="block text-2xs font-medium text-gray-700 mb-0.5">Dont TVA</label> <input type="text" name="tva" placeholder="ex: 20,00" value={formData.tva} onChange={handleChange} className={`w-full p-1.5 border rounded-md text-xs shadow-sm ${editErrors.tva ? 'border-red-500' : 'border-gray-300'}`} inputMode="decimal" /> {editErrors.tva && <p className="text-2xs text-red-500 mt-0.5">{editErrors.tva}</p>} </div>

                    <div> <label className="block text-2xs font-medium text-gray-400 mb-0.5">Total TTC</label> <input type="text" value={totalTTC} readOnly className="w-full p-1.5 bg-gray-100 border border-gray-300 rounded-md text-xs cursor-default" /> </div>

                  </div>

                </fieldset>

                <fieldset className="border p-2 rounded-md border-gray-300 bg-white">

                  <legend className="text-xs font-medium px-1 text-gray-600">Informations</legend>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">

                    <div> <label className="block text-2xs font-medium text-gray-700 mb-0.5">Date *</label> <input type="date" name="date" value={formData.date} onChange={handleChange} className={`w-full p-1.5 border rounded-md text-xs shadow-sm ${editErrors.date ? 'border-red-500' : 'border-gray-300'}`} required /> {editErrors.date && <p className="text-2xs text-red-500 mt-0.5">{editErrors.date}</p>} </div>

                    <div> <label className="block text-2xs font-medium text-gray-700 mb-0.5">Fournisseur</label> <input type="text" name="vendor" placeholder="Nom du fournisseur" value={formData.vendor} onChange={handleChange} className="w-full p-1.5 border border-gray-300 rounded-md text-xs shadow-sm" /> </div>

                  </div>

                  <div className="mt-2"> <label className="block text-2xs font-medium text-gray-700 mb-0.5">Description</label> <textarea name="description" placeholder="Courte description" value={formData.description} onChange={handleChange} rows="1" className="w-full p-1.5 border border-gray-300 rounded-md text-xs shadow-sm" /> </div>

                </fieldset>

                <fieldset className="border p-2 rounded-md border-gray-300 bg-white">

                  <legend className="text-xs font-medium px-1 text-gray-600">Fichier Justificatif</legend>

                  {(formData.file || formData.existingFilePath) && (<div className="flex items-center justify-between space-x-2 mb-1.5 p-1.5 bg-gray-50 rounded border border-gray-200"><div className="flex items-center space-x-1.5 min-w-0"><Paperclip className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" /><span className="text-xs text-gray-700 truncate" title={formData.file ? formData.file.name : formData.existingFilePath?.split('/').pop()}>{formData.file ? formData.file.name : formData.existingFilePath?.split('/').pop()}{formData.file && <span className="text-2xs text-blue-600 font-medium ml-1">(Nouveau)</span>}</span></div><button type="button" onClick={handleRemoveFile} className="text-red-500 hover:text-red-700 p-0.5 rounded-full hover:bg-red-100 flex-shrink-0" title="Supprimer/Changer"><XCircle className="h-3.5 w-3.5" /></button></div>)}

                  <label htmlFor={`file-upload-form-${initialData?.id || 'new'}-${employeeId}`} className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border cursor-pointer ${isAiExtracting || isUploading ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'}`}> <UploadCloud className="h-3.5 w-3.5 mr-1.5" /> <span>{formData.file || formData.existingFilePath ? 'Remplacer' : 'Choisir un fichier'}</span> </label>

                  <input id={`file-upload-form-${initialData?.id || 'new'}-${employeeId}`} type="file" name="file" onChange={handleFileChange} accept=".pdf, .jpeg, .jpg, .png" className="hidden" disabled={isAiExtracting || isUploading} />

                  {fileError && (<p className="mt-1 text-2xs text-red-600">{fileError}</p>)}

                </fieldset>

                <fieldset className="border p-2 rounded-md border-gray-300 bg-white">

                  <legend className="text-xs font-medium px-1 text-gray-600">Affectation Projets (Optionnel)</legend>

                  {editErrors.allocations && <p className="text-2xs text-red-500 mb-1">{editErrors.allocations}</p>}

                  <div className="space-y-1 mt-1 max-h-[60vh] min-h-[420px] overflow-y-auto">

                    {(formData.project_allocations || []).map((alloc, idx) => {

                      const projectKey = alloc.project_id || '';

                      const iterationInfo = iterationsByProject[projectKey] || {};

                      const iterationOptions = iterationInfo.items || [];

                      const iterationLoading = iterationInfo.loading;

                      const iterationError = iterationInfo.error;

                      const percentageValue = alloc.percentage === '' ? '' : alloc.percentage;

                      const amountInputValue = alloc.amount_input ?? '';

                      const canRemove = (formData.project_allocations || []).length > 1 || projectKey !== '' || percentageValue !== '';

                      const selectedProject = projectsById[projectKey];

                      const iterationKey = alloc.iteration_id ? String(alloc.iteration_id) : '';

                      const selectedIteration = iterationOptions.find(it => String(it.id) === iterationKey) || null;

                      const iterationStatusMeta = selectedIteration ? getIterationStatusMeta(selectedIteration) : null;

                      const projectVisionRaw = selectedProject?.vision ?? '';

                      const projectVision = typeof projectVisionRaw === 'string' ? projectVisionRaw.trim() : '';

                      const iterationVisionRaw = selectedIteration ? (selectedIteration.description ?? selectedIteration.vision ?? '') : '';

                      const iterationVision = typeof iterationVisionRaw === 'string' ? iterationVisionRaw.trim() : '';

                      return (

                        <div key={`alloc-form-${idx}`} className="rounded-lg border border-gray-200 bg-white shadow-sm p-2.5 space-y-2">

                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">

                            <div className="sm:flex-[1.1] min-w-[220px] space-y-1.5">

                              <span className="block text-2xs font-semibold uppercase tracking-wide text-gray-500">Projet</span>

                              <ProjectPicker

                                projects={projects || []}

                                value={alloc.project_id || ''}

                                onSelect={(projectId) => handleProjectSelect(idx, projectId)}

                                error={Boolean(editErrors.allocations && !alloc.project_id && percentageValue !== '')}

                              />

                              {selectedProject && (

                                <p className="text-2xs leading-snug text-gray-600 break-words">

                                  {projectVision ? projectVision : <span className="italic text-gray-400">Pas de vision renseignée pour ce projet.</span>}

                                </p>

                              )}

                            </div>

                            <div className="sm:flex-[1.1] min-w-[220px] space-y-1.5">

                              <span className="block text-2xs font-semibold uppercase tracking-wide text-gray-500">Itération</span>

                              <IterationPicker

                                iterations={iterationOptions}

                                value={alloc.iteration_id || ''}

                                onSelect={(iterationId) => handleIterationSelect(idx, iterationId)}

                                disabled={!alloc.project_id}

                                loading={iterationLoading}

                                error={iterationError}

                              />

                              {selectedIteration && (

                                <div className="flex items-start gap-1 min-w-0 text-2xs text-gray-600 leading-snug">

                                  {iterationStatusMeta && (

                                    <span className={`mt-0.5 px-2 py-0.5 rounded-full border ${iterationStatusMeta.badgeClass}`}>

                                      {iterationStatusMeta.label}

                                    </span>

                                  )}

                                  <p className="flex-1 min-w-0 break-words">

                                    {iterationVision ? iterationVision : <span className="italic text-gray-400">Pas de vision renseignée pour cette itération.</span>}

                                  </p>

                                </div>

                              )}

                            </div>

                            <div className="flex flex-wrap items-end gap-2 sm:flex-col sm:items-stretch sm:min-w-[200px] sm:gap-2">

                              <div className="flex flex-col min-w-[90px] space-y-1">

                                <span className="block text-2xs font-semibold uppercase tracking-wide text-gray-500">% allocation</span>

                                <div className="flex items-center px-2 py-1.5 border border-gray-300 rounded-md bg-white shadow-sm">

                                  <input

                                    type="number"

                                    min="0"

                                    max="100"

                                    step="1"

                                    value={percentageValue || ''}

                                    onChange={(e) => handlePercentageChange(idx, e.target.value)}

                                    className="w-full text-right text-sm bg-transparent outline-none"

                                  />

                                  <span className="ml-1 text-xs text-gray-500">%</span>

                                </div>

                              </div>

                              <div className="flex flex-col min-w-[130px] space-y-1">

                                <span className="block text-2xs font-semibold uppercase tracking-wide text-gray-500">Montant</span>

                                <div className={`flex items-center px-2 py-1.5 border rounded-md shadow-sm ${totalAmountValue ? 'border-gray-300 bg-white' : 'border-dashed border-gray-300 bg-gray-50'}`}>

                                  <input

                                    type="text"

                                    inputMode="decimal"

                                    value={amountInputValue}

                                    onChange={(e) => handleAllocationAmountChange(idx, e.target.value)}

                                    disabled={!totalAmountValue}

                                    className={`w-full text-right text-sm bg-transparent outline-none ${!totalAmountValue ? 'text-gray-400' : 'text-gray-900'}`}

                                  />

                                  <span className="ml-1 text-xs text-gray-500">{formData.currency}</span>

                                </div>

                              </div>

                              <div className="flex items-center justify-end w-full sm:w-auto sm:justify-start">

                                {canRemove ? (

                                  <button

                                    type="button"

                                    onClick={() => removeAllocation(idx)}

                                    className="text-red-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"

                                    title="Supprimer"

                                  >

                                    <Trash2 className="h-4 w-4" />

                                  </button>

                                ) : (

                                  <div className="w-[22px] h-[22px]" />

                                )}

                              </div>

                            </div>

                          </div>

                        </div>

                      );

                    })}

                  </div>

                  {allocationStats.hasProjects && (

                    <p className={`text-2xs mt-1 ${allocationStats.total === 100 ? 'text-green-600' : 'text-amber-600'}`}>

                      Total: {allocationStats.total}% {allocationStats.total === 100 ? '(équilibré)' : allocationStats.missing > 0 ? `(${allocationStats.missing}% manquants)` : `(${Math.abs(allocationStats.missing)}% en trop)`}

                    </p>

                  )}

                  <button type="button" onClick={addAllocation} className="mt-1.5 px-1.5 py-0.5 bg-gray-100 rounded-md text-2xs text-gray-700 hover:bg-gray-200 border border-gray-300"> + Projet </button>

                </fieldset>

                {editErrors.form && <p className="text-sm text-red-600 mt-2 text-right">{editErrors.form}</p>}

                <div className="relative z-20 flex justify-end space-x-2 pt-2">

                  <button type="button" onClick={onCancel} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50">Annuler</button>

                  <button type="submit" className="button-primary-xs" disabled={isUploading || isAiExtracting || !!fileError || Object.keys(editErrors).length > 0}>

                    {isUploading && <Loader2 className="animate-spin h-3.5 w-3.5 mr-1.5" />}

                    {isUploading ? (isEditing ? 'Sauvegarde...' : 'Envoi...') : (isEditing ? 'Enregistrer Modifs' : 'Ajouter Dépense')}

                  </button>

                </div>

              </fieldset>

            </form>

            <div className={`lg:w-1/3 p-3 flex-shrink-0 flex flex-col min-w-0 relative z-0`}>

              {(filePreviewUrl) && (

                <div className="flex flex-col h-full">

                    <div className="flex justify-between items-center mb-1 flex-shrink-0">

                        <h4 className="text-xs font-medium text-gray-600">Aperçu Justificatif</h4>

                        <button onClick={() => setShowPreview(prev => !prev)} className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" title={showPreview ? "Masquer" : "Afficher"}>

                            {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}

                        </button>

                    </div>

                    {showPreview && (

                        <div className="relative flex-grow min-h-[300px] border rounded-md bg-gray-100 overflow-hidden">

                            { (formData.file && formData.file.type.startsWith('image/')) || (formData.existingFilePath && /\.(jpe?g|png|gif)$/i.test(formData.existingFilePath)) ? (

                                <img src={filePreviewUrl} alt="Aperçu" className="w-full h-full object-contain"/>

                            ) : (formData.file && formData.file.type === 'application/pdf') || (formData.existingFilePath && /\.pdf$/i.test(formData.existingFilePath)) ? (

                                <iframe src={filePreviewUrl} title="Aperçu fichier" width="100%" height="100%" className="w-full h-full border-0"/>

                            ) : <p className="text-center text-xs text-gray-500 p-4">Aperçu non disponible pour ce type de fichier ou fichier manquant.</p>}

                        </div>

                    )}

                </div>

              )}

              {!filePreviewUrl && (

                <div className="flex-grow h-full flex flex-col items-center justify-center text-xs text-gray-400 italic border border-dashed rounded-md p-4 min-h-[300px]">

                    <UploadCloud className="h-8 w-8 mb-2 text-gray-300"/>

                    <span>Prévisualisation du fichier ici</span>

                </div>

                  )}

            </div>

          </div>

        </>

      )}

    </div>

  );

};

export default ExpenseForm;

